/**
 * SanPlumb — IngestaModal (manual)
 *
 * Modal del Paso 2: subida del plano del baño (referencia visual) y
 * declaración MANUAL de las medidas del recinto y el espesor del muro.
 * Los valores que el usuario confirma aquí construyen los muros virtuales
 * del visor 3D — no las dimensiones de la tabla §XI, que solo sirven como
 * sugerencia inicial.
 *
 * Sin IA: la lectura del plano la hace el usuario (decisión sellada v1.7).
 */

import { useState } from 'react';
import { Icon } from '../Icon';
import { tipologiaByCode, type TipologiaCode } from '../../constants/tipologias';
import type { RoomDims } from '../../types/ui';

export interface IngestaModalProps {
  /** Tipología activa — usada para pre-rellenar w/d/h sugeridos §XI. */
  tipologia: TipologiaCode;
  /** Dimensiones previas si el usuario ya pasó por la ingesta. */
  initialDims: RoomDims | null;
  onClose: () => void;
  /**
   * Confirma la ingesta con las dimensiones declaradas. El visor las
   * usará para construir los muros virtuales.
   */
  onConfirm: (dims: RoomDims) => void;
}

type Stage = 'upload' | 'dims';

export function IngestaModal({
  tipologia,
  initialDims,
  onClose,
  onConfirm,
}: IngestaModalProps): JSX.Element {
  const spec = tipologiaByCode(tipologia);
  const seed: RoomDims = initialDims ?? {
    w: spec.room.w,
    d: spec.room.d,
    h: spec.room.h,
    murW: 0.15,
    contrapiso: 0.05,
  };

  const [stage, setStage] = useState<Stage>('upload');
  const [fileName, setFileName] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Las medidas se editan en centímetros (más natural para gasfitería peruana)
  // y se convierten a metros al confirmar.
  const [wCm, setWCm] = useState<number>(Math.round(seed.w * 100));
  const [dCm, setDCm] = useState<number>(Math.round(seed.d * 100));
  const [hCm, setHCm] = useState<number>(Math.round(seed.h * 100));
  const [murCm, setMurCm] = useState<number>(Math.round(seed.murW * 100));
  const [contraCm, setContraCm] = useState<number>(Math.round(seed.contrapiso * 100));

  function handleFile(name: string): void {
    setFileName(name);
    setStage('dims');
  }

  function handleConfirm(): void {
    const dims: RoomDims = {
      w: Math.max(0.8, wCm / 100),
      d: Math.max(0.8, dCm / 100),
      h: Math.max(2.0, hCm / 100),
      murW: Math.max(0.06, murCm / 100),
      contrapiso: Math.max(0.02, contraCm / 100),
    };
    onConfirm(dims);
  }

  // Permitir saltar la subida del archivo: el plano es referencia visual,
  // pero las medidas se pueden declarar sin él.
  function skipUpload(): void {
    setFileName(null);
    setStage('dims');
  }

  const dimsValid =
    wCm >= 80 && dCm >= 80 && hCm >= 200 && murCm >= 6 && contraCm >= 2;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-ingesta" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hd">
          <div>
            <div className="eyebrow">PASO 02 · INGESTA + MEDIDAS DEL RECINTO</div>
            <h2 style={{ marginTop: 4 }}>Declarar medidas del baño</h2>
          </div>
          <button className="btn ghost" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <div className="modal-bd">
          {stage === 'upload' ? (
            <>
              <div
                className={`dropzone ${dragOver ? 'over' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const f = e.dataTransfer.files?.[0];
                  handleFile(f ? f.name : 'plano.jpg');
                }}
              >
                <div className="dz-ico">{Icon.upload}</div>
                <div className="dz-title">Arrastra el plano del baño aquí</div>
                <div className="dz-sub">JPG · PNG · PDF — referencia visual (opcional)</div>
                <label className="btn primary" style={{ marginTop: 14, cursor: 'pointer' }}>
                  Seleccionar archivo
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      handleFile(f ? f.name : 'plano.jpg');
                    }}
                  />
                </label>
                <button
                  className="btn ghost"
                  style={{ marginTop: 10 }}
                  onClick={skipUpload}
                >
                  Saltar — solo declarar medidas →
                </button>
              </div>
              <p className="notice" style={{ marginTop: 16 }}>
                <b>Cotas desde NPT ±0.00</b> — las alturas se miden desde el piso terminado, no
                desde la losa estructural. Las medidas que declares construyen los muros
                virtuales en el 3D.
              </p>
            </>
          ) : (
            <>
              {fileName && (
                <div className="ingesta-file">
                  {Icon.scan}
                  <span>{fileName}</span>
                  <span className="ia-status" style={{ marginLeft: 'auto' }}>
                    Plano de referencia
                  </span>
                </div>
              )}

              <div className="section-h" style={{ marginTop: 12 }}>
                Medidas del recinto (NPT ±0.00 → cielo terminado)
              </div>
              <div className="dims-hint">
                Sugerencia §XI · {spec.label}: {Math.round(spec.room.w * 100)}×
                {Math.round(spec.room.d * 100)}×{Math.round(spec.room.h * 100)} cm. Edita
                los valores para reflejar el baño real del plano subido.
              </div>

              <div className="dims-grid">
                <DimField
                  label="Ancho (X)"
                  hint="muro principal"
                  cm={wCm}
                  onChange={setWCm}
                  min={80}
                  max={800}
                />
                <DimField
                  label="Profundidad (Z)"
                  hint="muro lateral"
                  cm={dCm}
                  onChange={setDCm}
                  min={80}
                  max={800}
                />
                <DimField
                  label="Altura libre (Y)"
                  hint="NPT → cielo"
                  cm={hCm}
                  onChange={setHCm}
                  min={200}
                  max={400}
                />
                <DimField
                  label="Espesor muro"
                  hint="K.K. + tarrajeo"
                  cm={murCm}
                  onChange={setMurCm}
                  min={6}
                  max={40}
                />
                <DimField
                  label="Contrapiso"
                  hint="NPT → losa de piso"
                  cm={contraCm}
                  onChange={setContraCm}
                  min={2}
                  max={20}
                />
              </div>

              <div className="dot-row" style={{ marginTop: 14 }}>
                <span className="k">Área útil</span>
                <span className="v" style={{ fontFamily: 'var(--mono)' }}>
                  {((wCm * dCm) / 10000).toFixed(2)} m²
                </span>
              </div>
              <div className="dot-row">
                <span className="k">Volumen libre</span>
                <span className="v" style={{ fontFamily: 'var(--mono)' }}>
                  {((wCm * dCm * hCm) / 1_000_000).toFixed(2)} m³
                </span>
              </div>

              {!dimsValid && (
                <p className="notice err" style={{ marginTop: 12 }}>
                  <b>⚠ Medidas insuficientes</b> — mínimos: 80×80 cm planta, 200 cm altura,
                  6 cm de muro, 2 cm de contrapiso.
                </p>
              )}
            </>
          )}
        </div>

        <div className="modal-ft">
          {stage === 'dims' && (
            <button className="btn ghost" onClick={() => setStage('upload')}>
              ← Volver al plano
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button
            className="btn primary"
            disabled={stage === 'upload' || !dimsValid}
            onClick={handleConfirm}
          >
            Generar isométrico 3D {Icon.arrow}
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// DimField — input numérico con label y unidades
// ──────────────────────────────────────────────────────────────────

interface DimFieldProps {
  label: string;
  hint: string;
  cm: number;
  onChange: (cm: number) => void;
  min: number;
  max: number;
}

function DimField({ label, hint, cm, onChange, min, max }: DimFieldProps): JSX.Element {
  return (
    <label className="dim-field">
      <span className="dim-label">{label}</span>
      <span className="dim-hint">{hint}</span>
      <span className="dim-input-wrap">
        <input
          className="form-input"
          type="number"
          min={min}
          max={max}
          step={1}
          value={cm}
          onChange={(e) => {
            const n = Number(e.target.value);
            if (Number.isFinite(n)) onChange(n);
          }}
        />
        <span className="dim-unit">cm</span>
      </span>
    </label>
  );
}
